import {
  SymbolType,
  isDoorType,
  isDoubleDoorType,
  isCircleStairsType,
  isRectStairsType,
  isTurnStairsType,
  isSlidingDoorType,
  isSvgType,
  isVoidType,
  isWindowType,
} from "../../../../types/floorplannerTypes";
import {
  PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS,
  PRIMITIVE_SYMBOL_MODEL_KEYS,
  getPrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DModelKeyForSymbol,
} from "./primitiveSymbol3DConfig";

export type Symbol3DRenderMode = "opening" | "primitive" | "unsupported";

export type Symbol3DKind =
  | "door"
  | "slidingDoor"
  | "doubleDoor"
  | "circleStairs"
  | "rectStairs"
  | "turnStairs"
  | "window"
  | "void"
  | "primitiveSymbol"
  | "generic";

export type Symbol3DDefinition = {
  key: string;
  kind: Symbol3DKind;
  renderMode: Symbol3DRenderMode;
  source: "modelKey" | "libraryKey" | "legacy";
  supportsProductOverride: boolean;
};

type RegistryEntry = Omit<Symbol3DDefinition, "key" | "source">;

const primitiveSymbolRegistryEntries: Record<string, RegistryEntry> = {};

Array.from(PRIMITIVE_SYMBOL_MODEL_KEYS).forEach((key) => {
  primitiveSymbolRegistryEntries[key] = {
    kind: "primitiveSymbol",
    renderMode: "primitive",
    supportsProductOverride: true,
  };
});

Object.keys(PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS).forEach((key) => {
  primitiveSymbolRegistryEntries[key] = {
    kind: "primitiveSymbol",
    renderMode: "primitive",
    supportsProductOverride: true,
  };
});

const registryByKey: Record<string, RegistryEntry> = {
  "opening:door": {
    kind: "door",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "opening:slidingDoor": {
    kind: "slidingDoor",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "opening:doubleDoor": {
    kind: "doubleDoor",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "opening:window": {
    kind: "window",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "opening:void": {
    kind: "void",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/Door": {
    kind: "door",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/SlidingDoor": {
    kind: "slidingDoor",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/GlassSlidingDoor": {
    kind: "slidingDoor",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/DoubleDoor": {
    kind: "doubleDoor",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/Window": {
    kind: "window",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/void-symbol": {
    kind: "void",
    renderMode: "opening",
    supportsProductOverride: false,
  },
  "MANUAL/CircleStairs": {
    kind: "circleStairs",
    renderMode: "primitive",
    supportsProductOverride: false,
  },
  "MANUAL/RectStairs": {
    kind: "rectStairs",
    renderMode: "primitive",
    supportsProductOverride: false,
  },
  "MANUAL/TurnStairs": {
    kind: "turnStairs",
    renderMode: "primitive",
    supportsProductOverride: false,
  },
  circleStairs: {
    kind: "circleStairs",
    renderMode: "primitive",
    supportsProductOverride: false,
  },
  rectStairs: {
    kind: "rectStairs",
    renderMode: "primitive",
    supportsProductOverride: false,
  },
  turnStairs: {
    kind: "turnStairs",
    renderMode: "primitive",
    supportsProductOverride: false,
  },
  ...primitiveSymbolRegistryEntries,
};

export function getSymbol3DDefinition(symbol: SymbolType): Symbol3DDefinition {
  const modelKey = symbol.threeD?.modelKey;
  if (modelKey) {
    return createDefinition(modelKey, "modelKey", getRegistryEntry(modelKey));
  }

  if (symbol.libraryKey) {
    return createDefinition(
      symbol.libraryKey,
      "libraryKey",
      getRegistryEntry(symbol.libraryKey),
    );
  }

  const legacyKey = getLegacySymbol3DKey(symbol);
  return createDefinition(legacyKey, "legacy", getRegistryEntry(legacyKey));
}

export function isOpening3DDefinition(definition: Symbol3DDefinition): boolean {
  return definition.renderMode === "opening";
}

export function isLivingRoom3DDefinition(
  definition: Symbol3DDefinition,
): boolean {
  const modelKey = getPrimitiveSymbol3DModelKey(definition.key);
  return (
    definition.kind === "primitiveSymbol" &&
    modelKey !== undefined &&
    modelKey.startsWith("living:")
  );
}

export function isPrimitiveSymbol3DDefinition(
  definition: Symbol3DDefinition,
): boolean {
  return (
    definition.kind === "primitiveSymbol" &&
    getPrimitiveSymbol3DModelKey(definition.key) !== undefined
  );
}

function createDefinition(
  key: string,
  source: Symbol3DDefinition["source"],
  entry: RegistryEntry,
): Symbol3DDefinition {
  return {
    key,
    source,
    ...entry,
  };
}

function getRegistryEntry(key: string): RegistryEntry {
  return (
    registryByKey[key] ?? {
      kind: "generic",
      renderMode: "unsupported",
      supportsProductOverride: true,
    }
  );
}

function getLegacySymbol3DKey(symbol: SymbolType): string {
  if (isDoorType(symbol)) return "opening:door";
  if (isSlidingDoorType(symbol)) return "opening:slidingDoor";
  if (isDoubleDoorType(symbol)) return "opening:doubleDoor";
  if (isWindowType(symbol)) return "opening:window";
  if (isVoidType(symbol)) return "opening:void";
  if (isCircleStairsType(symbol)) return "circleStairs";
  if (isRectStairsType(symbol)) return "rectStairs";
  if (isTurnStairsType(symbol)) return "turnStairs";
  if (isSvgType(symbol)) {
    const primitiveModelKey = getPrimitiveSymbol3DModelKeyForSymbol(symbol);
    if (primitiveModelKey) return primitiveModelKey;
    return normalizeLegacySvgKey(
      symbol.libraryKey ?? symbol.name ?? symbol.svgPath,
    );
  }
  return symbol.type;
}

function normalizeLegacySvgKey(value: string | undefined): string {
  if (!value) return "svg:unknown";
  return `svg:${value.trim().toLowerCase()}`;
}
